import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { useNotifications } from '../../../common/hooks/useNotifications';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import Button from '../../../common/components/Button/Button';
import { CreateUpdateEmailConfigDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { EmailConfigFrom } from './EmailConfigForm';

interface EmailConfigEditorProps {
  id?: string;
  systemNumber?: number;
  emailConfig: CreateUpdateEmailConfigDto;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: (
    emailConfig: CreateUpdateEmailConfigDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  readOnly: boolean;
}

export const EmailConfigEditor: React.FC<EmailConfigEditorProps> = props => {
  const [emailConfig, setEmailConfig] = useState(props.emailConfig);
  const [show, setShow] = useState(false);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');

  const createEmailConfig = async () => {
    setIsLoading(true);
    props
      .onCompleted(emailConfig, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
        handleClose();
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  const createErrors = (errors: string[]) => {
    setSaveErrors(errors);
    setShowSaveErrors(!!errors.length);
  };

  const propertyChange = (obj: Partial<CreateUpdateEmailConfigDto>) => {
    setEmailConfig({ ...emailConfig, ...obj });
  };

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setEmailConfig(props.emailConfig);
      setShow(true);
    }
  }, [props.emailConfig, props.show]);

  const handleClose = () => {
    setShow(false);
    if (props.handleClose) {
      props.handleClose();
    }
  };

  return (
    <Modal
      onHide={handleClose}
      show={show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {props.id
            ? `${t(tk.administration.mailConfigEdit)} ${props.systemNumber}`
            : t(tk.administration.mailConfigAdd)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <EmailConfigFrom
          emailConfig={emailConfig}
          readOnly={props.readOnly}
          propertyChange={propertyChange}
          createErrors={createErrors}
        />
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="m-3"
        />
      </Modal.Body>
      <Modal.Footer>
        {props.id && (
          <LastEditorInfo
            id={props.id}
            method={pok.emailConfig.getLastEditor}
          />
        )}
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={isLoading}
        >
          {t(tk.button.close)}
        </Button>
        <Button
          variant="primary"
          onClick={createEmailConfig}
          isLoading={isLoading}
        >
          {t(tk.button.save)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
